import { useState, useRef, useEffect } from 'react'
import { Typography } from '~/components/common/Typography'
import { MuleRuntimeVersion } from '~/constants/settings'

interface RuntimeSettings {
  javaVersion: string
  mavenVersion: string
  muleRuntimeVersion: string
}

interface RuntimeSettingsSelectProps {
  value: RuntimeSettings
  onChange: (value: RuntimeSettings) => void
  disabled?: boolean
}

export function RuntimeSettingsSelect({
  value,
  onChange,
  disabled = false
}: RuntimeSettingsSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = (section: keyof RuntimeSettings, selectedValue: string) => {
    onChange({
      ...value,
      [section]: selectedValue
    })
  }

  const getDisplayText = () => {
    const parts = []
    if (value.javaVersion) parts.push(`Java ${value.javaVersion}`)
    if (value.mavenVersion) parts.push(`Maven ${value.mavenVersion}`)
    if (value.muleRuntimeVersion) parts.push(`Mule ${value.muleRuntimeVersion}`)

    if (parts.length === 0) return 'Select runtime settings'
    return parts.join(' • ')
  }

  const javaVersions = ['8.0', '11.0', '17.0']
  const mavenVersions = ['3.5', '3.8', '3.9']
  const muleVersions = Object.values(MuleRuntimeVersion)

  return (
    <div className='relative w-full' ref={dropdownRef}>
      {/* Label */}
      <Typography
        fontFamily='geistMono'
        fontSize='bodySm'
        fontWeight='semibold'
        className={disabled ? 'text-[rgba(0,0,0,0.45)]' : 'text-[rgba(0,0,0,0.65)]'}
        style={{ fontSize: '12px', fontWeight: 600, lineHeight: 'normal', marginBottom: '8px' }}
      >
        Runtime Settings (Select mvn, java, mule runtime version){' '}
        <span className='text-red-500'>*</span>
      </Typography>

      {/* Select Trigger */}
      <button
        type='button'
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full h-[48px] rounded-[12px] border px-[16px] py-[8px] text-left text-[14px] font-medium leading-[22px] flex items-center justify-between ${
          disabled
            ? 'border-[#D6D6D6] bg-[#F5F5F5] text-[rgba(0,0,0,0.45)] cursor-not-allowed'
            : isOpen
              ? 'border-[#9D7EE6] bg-white text-[rgba(0,0,0,0.88)] outline-none'
              : 'border-[#D6D6D6] bg-white text-[rgba(0,0,0,0.88)] hover:border-[#9D7EE6]'
        }`}
        style={{ fontFamily: 'Geist, sans-serif' }}
      >
        <span
          className={
            value.javaVersion || value.mavenVersion || value.muleRuntimeVersion
              ? ''
              : 'text-[rgba(0,0,0,0.25)]'
          }
        >
          {getDisplayText()}
        </span>
        <svg
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path
            d='M4 6L8 10L12 6'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {isOpen && !disabled && (
        <div className='absolute z-50 mt-[4px] w-full rounded-[12px] border border-[#EAEAEA] bg-white shadow-lg overflow-hidden max-h-[400px] overflow-y-auto'>
          {/* Java Version Section */}
          <div className='border-b border-[#EAEAEA]'>
            <div className='px-[12px] py-[8px] bg-white sticky top-0 z-10'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='semibold'
                className='text-[rgba(0,0,0,0.65)]'
                style={{ fontSize: '12px', fontWeight: 600 }}
              >
                Java Version
              </Typography>
            </div>
            <div className='py-[4px]'>
              {javaVersions.map(version => (
                <button
                  key={version}
                  type='button'
                  onClick={() => handleSelect('javaVersion', version)}
                  className={`w-full px-[16px] py-[8px] text-left text-[14px] font-medium leading-[22px] transition-colors flex items-center gap-[8px] ${
                    value.javaVersion === version
                      ? 'bg-[rgba(0,0,0,0.04)] text-[rgba(0,0,0,0.88)]'
                      : 'text-[rgba(0,0,0,0.88)] hover:bg-[rgba(0,0,0,0.04)]'
                  }`}
                  style={{ fontFamily: 'Geist, sans-serif' }}
                >
                  {/* Checkmark icon */}
                  <div className='w-[16px] h-[16px] flex items-center justify-center shrink-0'>
                    {value.javaVersion === version && (
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M13.3334 4L6.00002 11.3333L2.66669 8'
                          stroke='#000000'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>
                  <span>Java {version}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Maven Version Section */}
          <div className='border-b border-[#EAEAEA]'>
            <div className='px-[12px] py-[8px] bg-white sticky top-0 z-10'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='semibold'
                className='text-[rgba(0,0,0,0.65)]'
                style={{ fontSize: '12px', fontWeight: 600 }}
              >
                Maven Version
              </Typography>
            </div>
            <div className='py-[4px]'>
              {mavenVersions.map(version => (
                <button
                  key={version}
                  type='button'
                  onClick={() => handleSelect('mavenVersion', version)}
                  className={`w-full px-[16px] py-[8px] text-left text-[14px] font-medium leading-[22px] transition-colors flex items-center gap-[8px] ${
                    value.mavenVersion === version
                      ? 'bg-[rgba(0,0,0,0.04)] text-[rgba(0,0,0,0.88)]'
                      : 'text-[rgba(0,0,0,0.88)] hover:bg-[rgba(0,0,0,0.04)]'
                  }`}
                  style={{ fontFamily: 'Geist, sans-serif' }}
                >
                  {/* Checkmark icon */}
                  <div className='w-[16px] h-[16px] flex items-center justify-center shrink-0'>
                    {value.mavenVersion === version && (
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M13.3334 4L6.00002 11.3333L2.66669 8'
                          stroke='#000000'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>
                  <span>Maven {version}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mule Runtime Version Section */}
          <div>
            <div className='px-[12px] py-[8px] bg-white sticky top-0 z-10'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='semibold'
                className='text-[rgba(0,0,0,0.65)]'
                style={{ fontSize: '12px', fontWeight: 600 }}
              >
                Mule Runtime Version
              </Typography>
            </div>
            <div className='py-[4px]'>
              {muleVersions.map(version => (
                <button
                  key={version}
                  type='button'
                  onClick={() => handleSelect('muleRuntimeVersion', version)}
                  className={`w-full px-[16px] py-[8px] text-left text-[14px] font-medium leading-[22px] transition-colors flex items-center gap-[8px] ${
                    value.muleRuntimeVersion === version
                      ? 'bg-[rgba(0,0,0,0.04)] text-[rgba(0,0,0,0.88)]'
                      : 'text-[rgba(0,0,0,0.88)] hover:bg-[rgba(0,0,0,0.04)]'
                  }`}
                  style={{ fontFamily: 'Geist, sans-serif' }}
                >
                  {/* Checkmark icon */}
                  <div className='w-[16px] h-[16px] flex items-center justify-center shrink-0'>
                    {value.muleRuntimeVersion === version && (
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M13.3334 4L6.00002 11.3333L2.66669 8'
                          stroke='#000000'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>
                  <span>Mule {version}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
